import { CalendarFilled } from '@ant-design/icons'
import { DatePicker, Flex, Input, message, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { getTeamGroups } from '../../../api/aops.api'
import { getStudioList } from '../../../api/aops.vacation.api'
import {
  createWatchdog,
  deleteWatchdog,
  getWatchdogDetail,
  updateWatchdog,
} from '../../../api/watchdog.api'
import EmojiDropDown from '../../../components/apos/EmojiDropDown'
import '../../../components/apos/modal/aopsCommonModal.scss'
import AopsCommonModalLayout from '../../../components/apos/modal/AopsCommonModalLayout'
import { menuSelectedTeam } from '../../../store/aops'
import { trackerBoardRefetchTrigger } from '../../../store/standaloneSchedule'
import { userAuthStatus } from '../../../store/user'
const { Text } = Typography
const StandaloneWatchdogPopup = ({
  open,
  onClose,
  watchdogId,
  onSuccess,
  onOpenTaskPopup,
  onOpenTaskEditPopup,
  watchdogDetailRefetchTrigger = 0,
}) => {
  const [userAuth] = useRecoilState(userAuthStatus)
  const [selecteTeamByMenu] = useRecoilState(menuSelectedTeam)
  const refetchTrigger = useRecoilValue(trackerBoardRefetchTrigger)
  const [studioGroupId, setStudioGroupId] = useState(null)
  const [teamGroupId, setTeamGroupId] = useState(null)
  const [watchdogDetail, setWatchdogDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [emoji, setEmoji] = useState('⭐️')
  const [mustDoList, setMustDoList] = useState([])
  const [tempMustDo, setTempMustDo] = useState('')

  const [formData, setFormData] = useState({
    title: '',
    startDate: null,
    endDate: null,
    memo: '',
  })

  const isEditMode = !!watchdogId

  const fetchTeamGroups = async () => {
    const res = await getTeamGroups()
    const selectedTeamGroup = res?.find(
      (item) => item.name === userAuth.teamName,
    )
    setTeamGroupId(selectedTeamGroup?.id || null)
  }
  const fetchStudioGroups = async () => {
    const res = await getStudioList()
    const selectedStudioGroup = res?.find(
      (item) => item.name === userAuth.studioName,
    )
    setStudioGroupId(selectedStudioGroup?.id || null)
  }

  useEffect(() => {
    fetchStudioGroups()
    fetchTeamGroups()
  }, [])

  // Watchdog 상세 정보 가져오기
  const fetchWatchdogDetail = async () => {
    if (!watchdogId) return

    setLoading(true)
    try {
      const res = await getWatchdogDetail(watchdogId)
      setWatchdogDetail(res)
      setEmoji(res.icon || '⭐️')
      setMustDoList(
        res.checkLists?.map((c) => ({ id: c.id, title: c.title })) || [],
      )
      setFormData({
        title: res.title || '',
        startDate: res.startDate ? dayjs(res.startDate) : null,
        endDate: res.endDate ? dayjs(res.endDate) : null,
        memo: res.memo || '',
      })
    } catch (error) {
      console.log('error::', error)
      message.error('Weekly Goal 정보를 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && watchdogId) {
      fetchWatchdogDetail()
    } else if (open && !watchdogId) {
      // 생성 모드일 때 폼 초기화
      setWatchdogDetail(null)
      setEmoji('⭐️')
      setMustDoList([])
      setTempMustDo('')
      setFormData({
        title: '',
        startDate: null,
        endDate: null,
        memo: '',
      })
    }
  }, [open, watchdogId])

  // Task 생성/수정/삭제 후 Watchdog 상세 정보 다시 불러오기
  useEffect(() => {
    if (
      open &&
      watchdogId &&
      (refetchTrigger > 0 || watchdogDetailRefetchTrigger > 0)
    ) {
      fetchWatchdogDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchTrigger, watchdogDetailRefetchTrigger, open, watchdogId])

  const resetForm = (opts) => {
    setFormData({
      title: '',
      startDate: null,
      endDate: null,
      memo: '',
    })
    setWatchdogDetail(null)
    setEmoji('⭐️')
    setMustDoList([])
    setTempMustDo('')
    onClose?.(opts)
  }

  useEffect(() => {
    if (!open) {
      setFormData({
        title: '',
        startDate: null,
        endDate: null,
        memo: '',
      })
      setWatchdogDetail(null)
      setEmoji('⭐️')
      setMustDoList([])
      setTempMustDo('')
      setDeleting(false)
    }
  }, [open])

  const handleMustDoSubmit = () => {
    if (!tempMustDo.trim()) return
    setMustDoList((prev) => [...prev, { id: null, title: tempMustDo.trim() }])
    setTempMustDo('')
  }

  const removeMustDo = (index) => {
    setMustDoList((prev) => prev.filter((_, i) => i !== index))
  }

  const updateMustDoTitle = (index, title) => {
    setMustDoList((prev) =>
      prev.map((item, i) => (i === index ? { ...item, title } : item)),
    )
  }

  const validateForm = () => {
    if (!formData.title?.trim()) {
      message.error('업무 명을 입력해주세요')
      return false
    }
    if (!formData.startDate || !formData.endDate) {
      message.error('일정을 선택해주세요')
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    const basePayload = {
      title: formData.title.trim(),
      startDate: dayjs(formData.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(formData.endDate).format('YYYY-MM-DD'),
      teamGroupId: teamGroupId || watchdogDetail?.teamGroupId,
      studioGroupId: studioGroupId || watchdogDetail?.studioGroupId,
      icon: emoji,
      memo: '',
      isFixed: false,
    }

    try {
      if (isEditMode) {
        const payload = {
          ...basePayload,
          id: watchdogId,
          checkLists: mustDoList.map((item) => ({
            id: item.id,
            title: item.title,
          })),
        }
        await updateWatchdog(payload)
        message.success('Weekly Goal이 수정되었습니다.')
      } else {
        await createWatchdog({
          ...basePayload,
          checkListTitles: mustDoList.map((item) => item.title),
        })
        message.success('Weekly Goal이 생성되었습니다.')
      }
      resetForm()
      onSuccess?.()
    } catch (error) {
      console.log(error)
      message.error(
        isEditMode
          ? 'Weekly Goal 수정에 실패했습니다.'
          : 'Weekly Goal 생성에 실패했습니다.',
      )
    }
  }

  const handleDelete = async () => {
    if (!watchdogId || deleting) return

    setDeleting(true)
    try {
      await deleteWatchdog(watchdogId)
      message.success('Weekly Goal이 삭제되었습니다.')
      // 삭제 시 onSuccess() 호출 시 refetchTrigger가 올라가면서 아직 open인 상태로
      // fetchWatchdogDetail(삭제된 ID)이 호출되어 실패 메시지가 뜨므로,
      // onClose({ deleted: true })만 호출하고 부모에서 리스트 refetch 처리
      resetForm({ deleted: true })
      onClose({ deleted: true })
    } catch (error) {
      console.log(error)
      message.error('Weekly Goal 삭제에 실패했습니다.')
    } finally {
      setDeleting(false)
    }
  }

  const disabledStartDate = (current) => {
    if (!current) return false
    if (current < dayjs().startOf('day')) return true
    return formData.endDate ? current.isAfter(formData.endDate, 'day') : false
  }

  const disabledEndDate = (current) => {
    if (!current) return false
    if (current < dayjs().startOf('day')) return true
    return formData.startDate
      ? current.isBefore(formData.startDate, 'day')
      : false
  }

  return (
    <AopsCommonModalLayout
      title={isEditMode ? 'Weekly Goal 수정하기' : 'Weekly Goal 생성하기'}
      open={open}
      onClose={onClose}
    >
      <div className="form-container">
        <div className="form-wrapper">
          <div className="form-section">
            <label className="form-label">
              목표 명 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <Flex align="center" gap="8px">
                <EmojiDropDown
                  value={emoji}
                  defaultValue="⭐️"
                  onChange={setEmoji}
                  backgroundColor="#191A1C"
                />
                <Input
                  placeholder="이번주 우리의 목표를 적어주세요"
                  value={formData.title}
                  onChange={(e) => {
                    if (e.target.value.length > 50) return
                    setFormData({ ...formData, title: e.target.value })
                  }}
                  className="form-input"
                  // readOnly={true}
                />
              </Flex>
              <div className="example-text-group">
                <Text className="example-text">Weekly Goal이란?</Text>
                <Text
                  className="example-text"
                  style={{
                    fontSize: '12px',
                    color: '#585F65',
                    fontWeight: '400',
                  }}
                >
                  이번 주, 우리가 어디에 힘을 모을지 정하는 하나의 방향입니다.
                </Text>
              </div>
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">
              일정 <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ width: '100%' }}>
              <div
                className="date-picker-group"
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  width: '100%',
                  flexWrap: 'wrap',
                }}
              >
                <DatePicker
                  placeholder="날짜를 선택해주세요"
                  value={formData.startDate}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, startDate: date }))
                  }
                  className="form-input ant-picker"
                  format="YYYY-MM-DD"
                  suffixIcon={<CalendarFilled />}
                  disabledDate={disabledStartDate}
                  style={{ flex: 1, height: '48px' }}
                  readOnly={true}
                />
                <span style={{ color: '#383E44', fontWeight: 700 }}>~</span>
                <DatePicker
                  placeholder="날짜를 선택해주세요"
                  value={formData.endDate}
                  className="form-input ant-picker"
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, endDate: date }))
                  }
                  format="YYYY-MM-DD"
                  suffixIcon={<CalendarFilled />}
                  disabledDate={disabledEndDate}
                  style={{ flex: 1, height: '48px' }}
                  readOnly={true}
                />
              </div>
            </div>
          </div>
          <div className="form-section">
            <label className="form-label">Must-Do</label>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}
              >
                {mustDoList.map((item, index) => (
                  <Flex key={`mustdo-${index}`} align="center" gap="8px">
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        background: '#FFFFFF',
                        borderRadius: '8px',
                        border: '1px solid #E2EAEF',
                        height: '44px',
                        minWidth: 0,
                      }}
                    >
                      <Input
                        value={item.title}
                        variant="borderless"
                        style={{
                          flex: 0.8,
                          padding: 0,
                          minWidth: 0,
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#191a1c',
                        }}
                        onChange={(e) =>
                          updateMustDoTitle(index, e.target.value)
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMustDo(index)}
                      style={{
                        width: '44px',
                        height: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#E2EAEF',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#8A949E',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      ×
                    </button>
                  </Flex>
                ))}
                <Flex align="center" gap="8px">
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      background: '#FFFFFF',
                      borderRadius: '8px',
                      border: '1px solid #E2EAEF',
                      height: '44px',
                    }}
                  >
                    <Input
                      placeholder="Must-Do를 적어주세요"
                      value={tempMustDo}
                      variant="borderless"
                      style={{ flex: 1, padding: 0, minWidth: 0 }}
                      onChange={(e) => setTempMustDo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (e.nativeEvent.isComposing) return
                          handleMustDoSubmit()
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleMustDoSubmit}
                    disabled={!tempMustDo.trim()}
                    style={{
                      width: '52px',
                      height: '44px',
                      background: '#FFFFFF',
                      border: `1px solid ${tempMustDo.trim() ? '#007AFF' : '#D1D5DB'}`,
                      color: tempMustDo.trim() ? '#007AFF' : '#D1D5DB',
                      borderRadius: '8px',
                      cursor: tempMustDo.trim() ? 'pointer' : 'not-allowed',
                      fontWeight: '600',
                    }}
                  >
                    입력
                  </button>
                </Flex>
              </div>
              {/* <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    flex: 0.45,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    border: '1px solid #E2EAEF',
                    height: '44px',
                  }}
                >
                  <Input
                    placeholder="Must-Do를 적어주세요"
                    value={tempMustDo}
                    variant="borderless"
                    style={{ flex: 1, padding: 0, minWidth: 0 }}
                    onChange={(e) => setTempMustDo(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (e.nativeEvent.isComposing) return
                        handleMustDoSubmit()
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleMustDoSubmit}
                  disabled={!tempMustDo.trim()}
                  style={{
                    width: '52px',
                    height: '44px',
                    background: '#FFFFFF',
                    border: `1px solid ${tempMustDo.trim() ? '#007AFF' : '#D1D5DB'}`,
                    color: tempMustDo.trim() ? '#007AFF' : '#D1D5DB',
                    borderRadius: '8px',
                    cursor: tempMustDo.trim() ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                  }}
                >
                  입력
                </button>
              </div> */}
            </div>
          </div>

          {/* 포함된 Task 업무 */}
          {/* <div
            className="form-section vertical"
            style={!isEditMode ? { display: 'none' } : { display: 'flex' }}
          >
            <label className="form-label">포함된 Task 업무</label>
            <div style={{ width: '100%' }}>
              <div className="story-task-section">
                <div className="section-header">
                  <span className="section-title">
                    Watchdog 내 Task 업무 목록
                  </span>
                  <button
                    className="btn-add-task"
                    disabled={!isEditMode}
                    onClick={() => {
                      if (onOpenTaskPopup && isEditMode) {
                        onOpenTaskPopup()
                      }
                    }}
                  >
                    새로운 Task 생성 +
                  </button>
                </div>
                {watchdogDetail?.tasks && watchdogDetail.tasks.length > 0 ? (
                  <div className="task-card-list">
                    {watchdogDetail.tasks.map((task, index) => {
                      const isIssue =
                        task.endDateTime < dayjs().format('YYYY-MM-DD HH:mm:ss')
                      const colors = ['#FF7853', '#FFCA00', '#1FC297']
                      const taskColor = task.done
                        ? '#C7CFD7'
                        : colors[index % colors.length]
                      return (
                        <div
                          key={task.id}
                          className="story-task-card"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (onOpenTaskEditPopup) {
                              onOpenTaskEditPopup(task)
                            }
                          }}
                        >
                          <div
                            className="card-color-bar"
                            style={{ background: taskColor }}
                          />
                          <div className="card-content">
                            <div className="card-top">
                              <span
                                className="task-label"
                                style={{
                                  display: 'flex',
                                }}
                              >
                                <span className="task-label-text">Task</span>
                                {task.done && (
                                  <span
                                    className="task-label-done"
                                    style={{ color: '#9CA3AF' }}
                                  >
                                    &nbsp;|&nbsp;Done
                                  </span>
                                )}
                              </span>

                              <span style={{ marginLeft: 6, color: '#9CA3AF' }}>
                                <RightOutlined />
                              </span>
                            </div>
                            <div className="task-title">{task.title}</div>
                            <div className="task-date">
                              {dayjs(task.endDateTime).format('YY년 MM월 DD일')}
                            </div>
                            <div className="task-assignee">
                              담당자{' '}
                              <span className="name">
                                {task.managers
                                  ?.map((manager) => manager.userName)
                                  .join(', ') || '미지정'}
                              </span>
                            </div>
                            {isIssue && (
                              <Tag
                                icon={
                                  <FireFilled
                                    style={{
                                      color: task.done ? '#C7CFD7' : '#F25A67',
                                    }}
                                  />
                                }
                                className="task-issue-tag"
                                style={{
                                  borderColor: task.done
                                    ? '#C7CFD7'
                                    : '#F25A67',
                                  color: task.done ? '#C7CFD7' : '#F25A67',
                                }}
                              >
                                이슈
                              </Tag>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: '40px 24px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px',
                    }}
                  >
                    {isEditMode
                      ? 'Task 업무가 없습니다.'
                      : '워치도그 생성 후 Task 업무를 추가할 수 있습니다.'}
                  </div>
                )}
              </div>
            </div>
          </div> */}

          {/* <div className="form-section vertical">
            <label className="form-label">메모</label>
            <div className="textarea-with-counter">
              <Input.TextArea
                placeholder="업무 관련한 간단한 메모를 적어주세요"
                value={formData.memo}
                onChange={(e) => {
                  if (e.target.value.length > 200) return
                  setFormData({ ...formData, memo: e.target.value })
                }}
                style={{ height: '80px', resize: 'none' }}
              />
              <span className="char-counter">{formData.memo.length}/200</span>
            </div>
          </div> */}
        </div>

        <div className="popup-footer">
          {isEditMode ? (
            <button
              type="button"
              className="btn-cancel"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? '삭제 중...' : '삭제'}
            </button>
          ) : (
            <button className="btn-cancel" onClick={resetForm}>
              취소
            </button>
          )}
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEditMode ? '수정' : '등록'}
          </button>
        </div>
      </div>
    </AopsCommonModalLayout>
  )
}

export default StandaloneWatchdogPopup
